import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TimeToHireChart = () => {
  const data = [
    { month: 'Jan', current: 28, target: 30, industry: 32, applications: 180 },
    { month: 'Feb', current: 32, target: 30, industry: 33, applications: 195 },
    { month: 'Mar', current: 25, target: 30, industry: 31, applications: 220 },
    { month: 'Apr', current: 29, target: 30, industry: 34, applications: 240 },
    { month: 'May', current: 31, target: 30, industry: 30, applications: 210 },
    { month: 'Jun', current: 27, target: 30, industry: 29, applications: 260 },
    { month: 'Jul', current: 24, target: 30, industry: 31, applications: 285 },
    { month: 'Aug', current: 26, target: 30, industry: 28, applications: 270 },
    { month: 'Sep', current: 23, target: 30, industry: 32, applications: 295 },
    { month: 'Oct', current: 22, target: 30, industry: 30, applications: 310 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-slate-900 mb-2">{`${label} 2024`}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-slate-600">
                  {entry?.name === 'current' ? 'Our Performance' : 
                   entry?.name === 'target' ? 'Target Goal' : 'Industry Average'}:
                </span>
              </div>
              <span className="font-semibold text-slate-900">
                {entry?.value} days
              </span>
            </div>
          ))}
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Applications:</span>
              <span>{payload?.[0]?.payload?.applications}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const currentAverage = data?.reduce((sum, item) => sum + item?.current, 0) / data?.length;
  const targetAverage = data?.[0]?.target;
  const performance = ((targetAverage - currentAverage) / targetAverage * 100);

  return (
    <div className="space-y-6">
      {/* Header with KPIs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Time-to-Hire Trends</h3>
          <p className="text-sm text-slate-500">Average days from application to offer acceptance</p>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{currentAverage?.toFixed(1)}</div>
            <div className="text-xs text-slate-500">Avg. Days</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${performance > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
              {performance > 0 ? '+' : ''}{performance?.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500">vs Target</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="currentGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e2e8f0" 
              horizontal={true}
              vertical={false}
            />
            
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b' }}
            />
            
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b' }}
              domain={['dataMin - 2', 'dataMax + 2']}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* Target line */}
            <Line 
              type="monotone" 
              dataKey="target" 
              stroke="#94a3b8" 
              strokeWidth={2}
              strokeDasharray="8 4"
              dot={false}
              name="target"
            />
            
            {/* Industry average */}
            <Line 
              type="monotone" 
              dataKey="industry" 
              stroke="#f59e0b" 
              strokeWidth={2}
              dot={{ fill: '#f59e0b', strokeWidth: 0, r: 3 }}
              name="industry"
            />
            
            {/* Current performance - main line */}
            <Line 
              type="monotone" 
              dataKey="current" 
              stroke="#10b981" 
              strokeWidth={3}
              dot={{ fill: '#10b981', strokeWidth: 2, r: 5, stroke: '#ffffff' }}
              name="current"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
          <span className="text-slate-600">Our Performance</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-slate-400"></div>
          <span className="text-slate-600">Target (30 days)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
          <span className="text-slate-600">Industry Benchmark</span>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
        <div className="text-center p-3 bg-slate-50/60 rounded-lg">
          <div className="text-lg font-bold text-slate-900">15%</div>
          <div className="text-xs text-slate-500">Better than Q2</div>
        </div>
        <div className="text-center p-3 bg-slate-50/60 rounded-lg">
          <div className="text-lg font-bold text-emerald-600">22</div>
          <div className="text-xs text-slate-500">Best Month</div>
        </div>
        <div className="text-center p-3 bg-slate-50/60 rounded-lg">
          <div className="text-lg font-bold text-slate-900">9.2</div>
          <div className="text-xs text-slate-500">Std Deviation</div>
        </div>
        <div className="text-center p-3 bg-slate-50/60 rounded-lg">
          <div className="text-lg font-bold text-indigo-600">↗ 18%</div>
          <div className="text-xs text-slate-500">Improvement</div>
        </div>
      </div>
    </div>
  );
};

export default TimeToHireChart;